import React, { useEffect, useId, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import { playMiniTaskSound, prepareMiniTaskSound } from '../soundEffects';

const QUICK_MINUTES = [1, 2, 3, 5, 10];
const MINUTE_MS = 60_000;

const controlSx = {
  minHeight: { xs: 48, md: 54 },
  borderRadius: 99,
  px: { xs: 2, md: 2.5 },
  fontWeight: 1000,
  fontSize: { xs: 16, md: 19 },
  textTransform: 'none',
};

function formatTime(milliseconds) {
  const seconds = Math.ceil(Math.max(0, milliseconds) / 1000);
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

function Candle({ remainingMs, totalMs, phase, deadline }) {
  const finished = phase === 'complete';
  const candleRef = useRef(null);
  const remainingRef = useRef(remainingMs);
  const assetId = useId().replace(/:/g, '');
  const waxId = `timer-wax-${assetId}`;
  const clipId = `timer-clip-${assetId}`;
  remainingRef.current = remainingMs;

  useEffect(() => {
    let frame;
    const update = () => {
      const remaining = phase === 'running'
        ? Math.max(0, deadline - Date.now())
        : remainingRef.current;
      const fraction = totalMs ? Math.min(1, Math.max(0, remaining / totalMs)) : 1;
      // The wax, top surface, wick and flame share one translation and one paint.
      // A fixed clip preserves the candle's base without scaling any of its details.
      const meltedHeight = 184 - Math.max(12, 184 * fraction);
      candleRef.current.setAttribute('transform', `translate(0 ${meltedHeight})`);
      if (phase === 'running' && remaining > 0) frame = requestAnimationFrame(update);
    };
    update();
    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [deadline, phase, totalMs]);

  return (
    <Box
      component="svg"
      viewBox="0 0 320 300"
      aria-hidden="true"
      sx={{
        display: 'block',
        width: { xs: 260, sm: 280, md: 300 },
        height: { xs: 252, sm: 272, md: 284 },
        mx: 'auto',
      }}
    >
      <defs>
        <linearGradient id={waxId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ffd475" />
          <stop offset="30%" stopColor="#ffe5a0" />
          <stop offset="70%" stopColor="#ffda83" />
          <stop offset="100%" stopColor="#ffc65f" />
        </linearGradient>
        <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
          <path d="M92 -100 H228 V262 Q228 284 206 284 H114 Q92 284 92 262Z" />
        </clipPath>
      </defs>

      <path d="M40 108 Q40 120 28 122 Q40 124 40 136 Q42 124 54 122 Q42 120 40 108Z" fill="#c4b5fd" />
      <path d="M280 164 Q280 176 268 178 Q280 180 280 192 Q282 180 294 178 Q282 176 280 164Z" fill="#fcd77a" />
      <path d="M52 225 Q52 232 45 234 Q52 236 52 243 Q54 236 61 234 Q54 232 52 225Z" fill="#fcd77a" />
      <path d="M270 246 Q270 253 263 255 Q270 257 270 264 Q272 257 279 255 Q272 253 270 246Z" fill="#d8b4fe" />
      <ellipse cx="160" cy="290" rx="98" ry="10" fill="#d8b4fe" />

      <g clipPath={`url(#${clipId})`}>
        <g
          ref={candleRef}
          data-testid="timer-candle"
          transform="translate(0 0)"
          style={{ transition: 'none' }}
        >
          <rect data-testid="timer-candle-wax" x="92" y="100" width="136" height="184" rx="22" fill={`url(#${waxId})`} />
          <rect x="108" y="121" width="9" height="151" rx="4.5" fill="#fff" opacity="0.25" />
          <path transform="translate(92 100)" d="M0 0 H136 V19 Q136 27 128 27 Q120 27 120 19 V17 Q120 12 114 12 Q106 12 106 24 V31 Q106 43 97 43 Q88 43 88 31 V28 Q88 20 80 20 Q72 20 72 30 V39 Q72 52 63 52 Q54 52 54 39 V27 Q54 18 46 18 Q37 18 37 29 V44 Q37 61 27 61 Q17 61 17 44 V22 Q17 16 10 16 Q3 16 0 23Z" fill="#fff0bb" />
          <ellipse data-testid="timer-candle-surface" cx="160" cy="100" rx="68" ry="11" fill="#fff3cb" />
          <ellipse cx="160" cy="100" rx="53" ry="5" fill="#ffe4a0" />
          <g transform="translate(119 9)">
            <Box
              component="g"
              data-testid="timer-candle-flame"
              sx={{
                opacity: finished ? 0 : 1,
                transition: 'opacity 550ms ease',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}
            >
              <path d="M42 4 C48 20 63 26 63 46 C63 62 53 72 41 72 C28 72 18 62 18 48 C18 32 35 24 35 8 Q36 0 42 4Z" fill="#ffb52e" />
              <path d="M41 23 C45 36 54 42 54 53 C54 65 48 71 41 71 C33 71 27 64 27 55 C27 45 38 36 41 23Z" fill="#ffe682" />
              <path d="M41 49 C43 57 48 61 48 66 C48 71 45 74 41 74 C36 74 33 71 33 66 C33 60 39 57 41 49Z" fill="#fff5c6" />
            </Box>
            <rect x="39" y="73" width="4" height="18" rx="2" fill="#795339" />
          </g>
        </g>
      </g>
    </Box>
  );
}

export default function ClassroomTimerModal({ onClose, soundEnabled = true }) {
  const [phase, setPhase] = useState('setup');
  const [selectedDuration, setSelectedDuration] = useState(3);
  const [customMinutes, setCustomMinutes] = useState('3');
  const [customSeconds, setCustomSeconds] = useState('0');
  const [remainingMs, setRemainingMs] = useState(3 * MINUTE_MS);
  const [totalMs, setTotalMs] = useState(3 * MINUTE_MS);
  const deadlineRef = useRef(null);
  const completionSoundPlayedRef = useRef(false);

  const customTotalSeconds = Number(customMinutes) * 60 + Number(customSeconds);
  const customValid = customMinutes !== '' && customSeconds !== ''
    && Number.isInteger(Number(customMinutes)) && Number.isInteger(Number(customSeconds))
    && Number(customMinutes) >= 0 && Number(customMinutes) <= 120
    && Number(customSeconds) >= 0 && Number(customSeconds) <= 59
    && customTotalSeconds > 0 && customTotalSeconds <= 120 * 60;
  const durationMs = selectedDuration === 'custom'
    ? customTotalSeconds * 1000
    : selectedDuration * MINUTE_MS;

  useEffect(() => {
    if (phase !== 'running') return undefined;
    const update = () => {
      const next = Math.max(0, deadlineRef.current - Date.now());
      setRemainingMs(next);
      if (next === 0) setPhase('complete');
    };
    update();
    const interval = setInterval(update, 100);
    return () => clearInterval(interval);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'complete' || completionSoundPlayedRef.current) return;
    completionSoundPlayedRef.current = true;
    if (soundEnabled) playMiniTaskSound('complete', true);
  }, [phase, soundEnabled]);

  const start = () => {
    if (!durationMs || (selectedDuration === 'custom' && !customValid)) return;
    // Unlock browser audio during the teacher's click, before the timer expires.
    prepareMiniTaskSound(soundEnabled);
    completionSoundPlayedRef.current = false;
    deadlineRef.current = Date.now() + durationMs;
    setTotalMs(durationMs);
    setRemainingMs(durationMs);
    setPhase('running');
  };

  const pause = () => {
    const next = Math.max(0, deadlineRef.current - Date.now());
    setRemainingMs(next);
    setPhase(next === 0 ? 'complete' : 'paused');
  };

  const resume = () => {
    prepareMiniTaskSound(soundEnabled);
    deadlineRef.current = Date.now() + remainingMs;
    setPhase('running');
  };

  const addMinute = () => {
    if (phase === 'running') {
      deadlineRef.current += MINUTE_MS;
      setRemainingMs(Math.max(0, deadlineRef.current - Date.now()));
    } else {
      setRemainingMs((current) => current + MINUTE_MS);
    }
    setTotalMs((current) => current + MINUTE_MS);
  };

  const end = () => {
    deadlineRef.current = null;
    setPhase('setup');
  };

  return (
    <Modal open onClose={onClose} aria-labelledby="classroom-timer-title">
      <Box
        onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
        sx={{
          position: 'fixed', inset: 0, bgcolor: 'rgba(23,19,39,0.46)',
          backdropFilter: 'blur(1.5px)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', p: { xs: 1, sm: 2.2 },
        }}
      >
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby="classroom-timer-title"
          sx={{
            position: 'relative', width: 'min(94vw, 610px)', maxHeight: '92vh',
            overflowX: 'hidden', overflowY: 'auto', borderRadius: { xs: 4, md: 5 },
            bgcolor: 'rgba(255,255,255,0.96)', border: '4px solid #d8c8ff',
            boxShadow: '0 7px 0 rgba(124,58,237,0.24), 0 28px 52px rgba(23,19,39,0.34), inset 0 0 26px rgba(167,139,250,0.16)',
            px: { xs: 1.5, sm: 2.5, md: 3 }, pt: { xs: 2.4, md: 2.7 }, pb: { xs: 2, md: 2.4 },
            textAlign: 'center',
            backgroundImage: 'radial-gradient(circle at 18% 20%, rgba(236,72,153,0.08) 0 52px, transparent 53px), radial-gradient(circle at 84% 23%, rgba(167,139,250,0.14) 0 62px, transparent 63px), linear-gradient(180deg, rgba(255,255,255,0.98), rgba(250,245,255,0.96))',
          }}
        >
          <IconButton
            aria-label="Zamanlayıcıyı kapat"
            onClick={onClose}
            sx={{
              position: 'absolute', top: { xs: 11, md: 14 }, right: { xs: 11, md: 14 },
              width: { xs: 43, md: 49 }, height: { xs: 43, md: 49 },
              bgcolor: '#b285ff', color: '#fff', border: '3px solid rgba(255,255,255,0.88)',
              boxShadow: '0 4px 0 rgba(91,33,182,0.24)', '&:hover': { bgcolor: '#9f6df5' },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
          </IconButton>

          <Typography
            id="classroom-timer-title"
            sx={{ color: '#4c1d95', fontWeight: 1000, fontSize: { xs: 34, sm: 42, md: 46 }, lineHeight: 1, px: 5, textShadow: '0 3px 0 rgba(255,255,255,0.95)' }}
          >
            Zamanlayıcı
          </Typography>

          {phase === 'setup' ? (
            <Box sx={{ mt: { xs: 2.2, md: 3 } }}>
              <Typography sx={{ color: '#242061', fontWeight: 900, fontSize: { xs: 20, md: 25 } }}>
                Süre seçin
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.2 }}>
                {QUICK_MINUTES.map((minutes) => (
                  <Button
                    key={minutes}
                    variant={selectedDuration === minutes ? 'contained' : 'outlined'}
                    aria-pressed={selectedDuration === minutes}
                    onClick={() => setSelectedDuration(minutes)}
                    sx={{ ...controlSx, minWidth: 110, bgcolor: selectedDuration === minutes ? '#7c3aed' : '#fff', color: selectedDuration === minutes ? '#fff' : '#5b21b6', borderColor: '#7c3aed', '&:hover': { bgcolor: selectedDuration === minutes ? '#6d28d9' : '#faf5ff', borderColor: '#6d28d9' } }}
                  >
                    {minutes} dakika
                  </Button>
                ))}
                <Button
                  variant={selectedDuration === 'custom' ? 'contained' : 'outlined'}
                  aria-pressed={selectedDuration === 'custom'}
                  onClick={() => setSelectedDuration('custom')}
                  sx={{ ...controlSx, minWidth: 160, bgcolor: selectedDuration === 'custom' ? '#7c3aed' : '#fff', color: selectedDuration === 'custom' ? '#fff' : '#5b21b6', borderColor: '#7c3aed', '&:hover': { bgcolor: selectedDuration === 'custom' ? '#6d28d9' : '#faf5ff', borderColor: '#6d28d9' } }}
                >
                  Özel süre
                </Button>
              </Box>
              {selectedDuration === 'custom' && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1.5 }}>
                  <TextField label="Dakika" type="number" value={customMinutes} onChange={(event) => setCustomMinutes(event.target.value)} inputProps={{ min: 0, max: 120, step: 1 }} sx={{ width: 120 }} />
                  <TextField label="Saniye" type="number" value={customSeconds} onChange={(event) => setCustomSeconds(event.target.value)} inputProps={{ min: 0, max: 59, step: 1 }} sx={{ width: 120 }} />
                </Box>
              )}
              <Button
                variant="contained"
                startIcon={<PlayArrowRoundedIcon />}
                onClick={start}
                disabled={selectedDuration === 'custom' && !customValid}
                sx={{ ...controlSx, mt: 3, minWidth: 220, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}
              >
                Başlat
              </Button>
            </Box>
          ) : (
            <Box sx={{ mt: { xs: 0.8, md: 1.2 } }}>
              <Box>
                <Candle remainingMs={remainingMs} totalMs={totalMs} phase={phase} deadline={deadlineRef.current} />
                <Box>
                  <Typography
                    role="timer"
                    aria-live="off"
                    sx={{ color: '#242061', fontWeight: 1000, fontSize: { xs: 60, sm: 76, md: 88 }, lineHeight: 1.05, fontVariantNumeric: 'tabular-nums', letterSpacing: 1 }}
                  >
                    {formatTime(remainingMs)}
                  </Typography>
                  <Typography aria-live="polite" sx={{ mt: phase === 'running' ? 0 : 1, color: '#5b21b6', fontWeight: 900, fontSize: { xs: 20, md: 26 } }}>
                    {phase === 'complete' ? 'Süre tamamlandı' : phase === 'paused' ? 'Duraklatıldı' : ''}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.2 }}>
                {phase === 'running' && <Button variant="contained" startIcon={<PauseRoundedIcon />} onClick={pause} sx={{ ...controlSx, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>Duraklat</Button>}
                {phase === 'paused' && <Button variant="contained" startIcon={<PlayArrowRoundedIcon />} onClick={resume} sx={{ ...controlSx, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>Devam Et</Button>}
                {phase !== 'complete' && <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={addMinute} sx={{ ...controlSx, bgcolor: '#fff', color: '#5b21b6', borderColor: '#7c3aed', '&:hover': { borderColor: '#6d28d9', bgcolor: '#faf5ff' } }}>+1 dakika</Button>}
                <Button variant="outlined" startIcon={<StopRoundedIcon />} onClick={end} sx={{ ...controlSx, bgcolor: '#fff', color: '#5b21b6', borderColor: '#7c3aed', '&:hover': { borderColor: '#6d28d9', bgcolor: '#faf5ff' } }}>
                  {phase === 'complete' ? 'Yeni Zamanlayıcı' : 'Bitir'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
